/**
 * Planes por etapas y metas del día (design.md, 8.8 y 8.9; Req. 21 y 22.3).
 *
 * Código puro. Las reglas que protegen el historial:
 * - Las metas de un día salen de la etapa que lo cubre, y las etapas pasadas
 *   no se modifican: editar metas "desde hoy" parte la etapa en curso.
 * - El mantenimiento es un historial por fecha: el vigente en un día es el de
 *   mayor `desde` que no se rechazó. Uno nuevo no cambia los días anteriores.
 * - Nada se supone: si una meta depende del mantenimiento y no hay, la meta
 *   queda sin dato con el motivo "falta el mantenimiento".
 */

import type {
  EtapaPlantilla,
  MacrosDiarios,
  Maintenance,
  MetaKcal,
  PeriodoMetasManuales,
  Plan,
  Stage,
  TipoEtapa,
} from '@/db/types';
import { diferenciaDias, esFechaLocal, formatearCortoConAnio, formatearLargo, sumarDias, type FechaLocal } from './dates';
import { formatearNumero, redondearAMultiplo } from './numbers';
import { formatearGramos, formatearKcal } from './nutricion';
import { normalizar } from './busqueda';
import type { FranjaGrafica } from './grafica';

/** Se programan ciclos completos durante al menos un año (Req. 21.4). */
export const DIAS_A_PROGRAMAR = 364;
/** Aviso de "faltan 4 semanas" para el final de lo programado. */
export const DIAS_AVISO_FIN = 28;
/** Días que se avisa de una etapa nueva mientras no se marque como vista (Req. 21.8). */
export const DIAS_AVISO_ETAPA = 7;
/** Lo más que dura una etapa, también al cambiar su fin (como en la plantilla). */
export const SEMANAS_MAXIMAS_ETAPA = 104;

export const NOMBRE_ETAPA: Readonly<Record<TipoEtapa, string>> = {
  deficit: 'Déficit',
  mantenimiento: 'Mantenimiento',
  superavit: 'Superávit',
};

export type MetasEtapa = Pick<
  EtapaPlantilla,
  'metaKcal' | 'proteinaG' | 'carbohidratosG' | 'grasaG' | 'ayunoHoras'
>;

/** Una etapa con fechas, todavía sin id (lo pone quien la guarda). */
export interface EtapaProgramada extends MetasEtapa {
  ciclo: number;
  indice: number;
  tipo: TipoEtapa;
  fechaInicio: FechaLocal;
  fechaFin: FechaLocal | null;
}

/** Lo que las funciones de aquí leen de una etapa guardada. */
export type EtapaConFechas = Pick<
  Stage,
  'planId' | 'ciclo' | 'indice' | 'tipo' | 'fechaInicio' | 'fechaFin'
> &
  MetasEtapa;

// ---------------------------------------------------------------------------
// Plantillas (Req. 21.3)
// ---------------------------------------------------------------------------

export interface PlantillaPlan {
  id: string;
  nombre: string;
  descripcion: string;
  repetir: boolean;
  etapas: EtapaPlantilla[];
}

const sinMetas = { proteinaG: null, carbohidratosG: null, grasaG: null } as const;

/**
 * Disponibles en cada teléfono; ninguna se activa sola. Crear un plan copia
 * la plantilla. La meta de kcal del déficit se define al crear el plan.
 */
export const PLANTILLAS: readonly PlantillaPlan[] = [
  {
    id: 'deficit_6_2',
    nombre: 'Déficit intermitente 6 + 2',
    descripcion: '6 semanas de déficit y 2 de mantenimiento, con ayuno de 12 h en ambas, repetido.',
    repetir: true,
    etapas: [
      { tipo: 'deficit', semanas: 6, metaKcal: null, ...sinMetas, ayunoHoras: 12 },
      {
        tipo: 'mantenimiento',
        semanas: 2,
        metaKcal: { modo: 'diferencia', kcal: 0 },
        ...sinMetas,
        ayunoHoras: 12,
      },
    ],
  },
  {
    id: 'deficit_continuo',
    nombre: 'Déficit continuo',
    descripcion: 'Una sola etapa de déficit, sin fecha de fin.',
    repetir: false,
    etapas: [{ tipo: 'deficit', semanas: null, metaKcal: null, ...sinMetas, ayunoHoras: null }],
  },
  {
    id: 'mantenimiento',
    nombre: 'Mantenimiento',
    descripcion: 'Comer lo de tu mantenimiento, sin fecha de fin.',
    repetir: false,
    etapas: [
      {
        tipo: 'mantenimiento',
        semanas: null,
        metaKcal: { modo: 'diferencia', kcal: 0 },
        ...sinMetas,
        ayunoHoras: null,
      },
    ],
  },
  {
    id: 'superavit',
    nombre: 'Superávit',
    descripcion: 'Una etapa de superávit para ganar peso, sin fecha de fin.',
    repetir: false,
    etapas: [{ tipo: 'superavit', semanas: null, metaKcal: null, ...sinMetas, ayunoHoras: null }],
  },
];

export function plantillaPorId(id: string): PlantillaPlan | undefined {
  return PLANTILLAS.find((p) => p.id === id);
}

/**
 * La configuración aprobada por Janeth (requirements.md, "Configuración
 * inicial del perfil de Janeth"). Solo prellena el formulario en su teléfono;
 * ella lo revisa y lo crea. Las metas del mantenimiento quedan pendientes: su
 * kcal sale del mantenimiento que ella capture.
 */
export const PLAN_DE_JANETH: {
  readonly plantilla: string;
  readonly fechaInicio: FechaLocal;
  /** En el orden de la plantilla: déficit y mantenimiento. */
  readonly metas: readonly MetasEtapa[];
} = {
  plantilla: 'deficit_6_2',
  fechaInicio: '2026-10-12',
  metas: [
    { metaKcal: { modo: 'fija', kcal: 1500 }, proteinaG: 140, carbohidratosG: 135, grasaG: 45, ayunoHoras: 12 },
    { metaKcal: { modo: 'diferencia', kcal: 0 }, proteinaG: null, carbohidratosG: null, grasaG: null, ayunoHoras: 12 },
  ],
};

/**
 * El plan acordado de Janeth en palabras, para ofrecerlo en su teléfono sin
 * crearlo solo (0.3.8; su respaldo del 27 oct 2026 no tenía plan ni metas):
 * "6 semanas de déficit (1,500 kcal, 140 g de proteína, …) y 2 de
 * mantenimiento, desde el lunes 12 de octubre de 2026, repetido."
 */
export function textoDelPlanDeJaneth(): string {
  const etapas = plantillaPorId(PLAN_DE_JANETH.plantilla)?.etapas ?? [];
  const deficit = PLAN_DE_JANETH.metas[0];
  const partes = [
    deficit?.metaKcal?.modo === 'fija' ? formatearKcal(deficit.metaKcal.kcal) : null,
    deficit?.proteinaG != null ? `${formatearGramos(deficit.proteinaG)} de proteína` : null,
    deficit?.carbohidratosG != null ? `${formatearGramos(deficit.carbohidratosG)} de carbohidratos` : null,
    deficit?.grasaG != null ? `${formatearGramos(deficit.grasaG)} de grasa` : null,
    deficit?.ayunoHoras != null ? `${formatearNumero(deficit.ayunoHoras, { decimales: 1, fijos: false })} h de ayuno` : null,
  ].filter((p): p is string => p !== null);
  const metas = partes.length <= 1 ? partes.join('') : `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}`;
  const semanas = (i: number) => etapas[i]?.semanas ?? null;
  return `${semanas(0) ?? 6} semanas de déficit (${metas}) y ${semanas(1) ?? 2} de mantenimiento, desde el ${formatearLargo(
    PLAN_DE_JANETH.fechaInicio,
  )}, repetido. Las metas del mantenimiento quedan pendientes: las defines tú.`;
}

/** El teléfono de Janeth es el que tiene su nombre en el perfil (Janeth o Yaneth). */
export function esPerfilDeJaneth(nombre: string | null): boolean {
  if (nombre === null) return false;
  return /^(janeth|yaneth)(\s|$)/.test(normalizar(nombre));
}

// ---------------------------------------------------------------------------
// Validación
// ---------------------------------------------------------------------------

const enRango = (v: number | null, min: number, max: number) =>
  v === null || (Number.isFinite(v) && v >= min && v <= max);

export function validarMetaKcal(meta: MetaKcal | null): string | null {
  if (meta === null) return null;
  if (meta.modo === 'fija' && !enRango(meta.kcal, 500, 10_000)) {
    return 'La meta fija debe estar entre 500 y 10,000 kcal.';
  }
  if (meta.modo === 'diferencia' && !enRango(meta.kcal, -3000, 3000)) {
    return 'La diferencia debe estar entre −3,000 y 3,000 kcal.';
  }
  if (meta.modo === 'porcentaje' && !enRango(meta.pct, -50, 50)) {
    return 'El porcentaje debe estar entre −50 y 50.';
  }
  return null;
}

export function validarMetas(m: MetasEtapa): string | null {
  const kcal = validarMetaKcal(m.metaKcal);
  if (kcal !== null) return kcal;
  if (![m.proteinaG, m.carbohidratosG, m.grasaG].every((g) => enRango(g, 0, 1000))) {
    return 'Los gramos deben estar entre 0 y 1,000.';
  }
  if (!enRango(m.ayunoHoras, 0, 48)) return 'Las horas de ayuno deben estar entre 0 y 48.';
  return null;
}

/** null si la plantilla se puede programar. */
export function validarPlantilla(etapas: readonly EtapaPlantilla[], repetir: boolean): string | null {
  if (etapas.length === 0) return 'El plan necesita al menos una etapa.';
  for (const [i, e] of etapas.entries()) {
    if (
      e.semanas !== null &&
      !(Number.isInteger(e.semanas) && e.semanas >= 1 && e.semanas <= SEMANAS_MAXIMAS_ETAPA)
    ) {
      return `Cada etapa dura de 1 a ${SEMANAS_MAXIMAS_ETAPA} semanas.`;
    }
    if (e.semanas === null && i < etapas.length - 1) return 'Solo la última etapa puede no tener fin.';
    const metas = validarMetas(e);
    if (metas !== null) return metas;
  }
  if (repetir && etapas.some((e) => e.semanas === null)) {
    return 'Para repetir el plan, todas las etapas necesitan su duración en semanas.';
  }
  return null;
}

// ---------------------------------------------------------------------------
// Calendario (design.md, 8.8)
// ---------------------------------------------------------------------------

/**
 * Etapas con fechas desde `inicio`. Con repetir, programa ciclos completos
 * hasta pasar `hasta` (un año por omisión): nunca deja un ciclo a medias.
 */
export function programar(
  plantilla: readonly EtapaPlantilla[],
  repetir: boolean,
  inicio: FechaLocal,
  opciones: { cicloInicial?: number; hasta?: FechaLocal } = {},
): EtapaProgramada[] {
  const error = validarPlantilla(plantilla, repetir);
  if (error !== null) throw new RangeError(error);
  const hasta = opciones.hasta ?? sumarDias(inicio, DIAS_A_PROGRAMAR);
  const etapas: EtapaProgramada[] = [];
  let cursor = inicio;
  let ciclo = opciones.cicloInicial ?? 1;

  for (;;) {
    for (const [indice, p] of plantilla.entries()) {
      const fechaFin = p.semanas === null ? null : sumarDias(cursor, p.semanas * 7 - 1);
      etapas.push({
        ciclo,
        indice,
        tipo: p.tipo,
        fechaInicio: cursor,
        fechaFin,
        metaKcal: p.metaKcal,
        proteinaG: p.proteinaG,
        carbohidratosG: p.carbohidratosG,
        grasaG: p.grasaG,
        ayunoHoras: p.ayunoHoras,
      });
      if (fechaFin === null) return etapas;
      cursor = sumarDias(fechaFin, 1);
    }
    if (!repetir || cursor > hasta) return etapas;
    ciclo += 1;
  }
}

/** La etapa que cubre una fecha. */
export function etapaDe<T extends Pick<Stage, 'fechaInicio' | 'fechaFin'>>(
  etapas: readonly T[],
  fecha: FechaLocal,
): T | null {
  return etapas.find((e) => e.fechaInicio <= fecha && (e.fechaFin === null || fecha <= e.fechaFin)) ?? null;
}

/** El último día programado de un plan; null si alguna etapa no tiene fin. */
export function finProgramado(etapas: readonly Pick<Stage, 'fechaFin'>[]): FechaLocal | null {
  let fin: FechaLocal | null = null;
  for (const e of etapas) {
    if (e.fechaFin === null) return null;
    if (fin === null || e.fechaFin > fin) fin = e.fechaFin;
  }
  return fin;
}

export interface PosicionEnPlan {
  planId: string;
  tipo: TipoEtapa;
  ciclo: number;
  /** Lugar de la etapa en la plantilla del plan. */
  indice: number;
  /** Primer día de la etapa, aunque se haya partido al editar metas. */
  inicio: FechaLocal;
  /** Semana de la etapa, contada desde su inicio original aunque se haya partido. */
  semana: number;
  semanasTotales: number | null;
  diaDelCiclo: number;
  /** Días que faltan contando el de hoy; null si la etapa no tiene fin. */
  diasQueFaltan: number | null;
  /** La etapa que sigue, si está programada. */
  siguiente: TipoEtapa | null;
  /** true el primer día de la etapa (Req. 21.8). */
  empiezaHoy: boolean;
}

/**
 * Dónde va una fecha dentro de su plan (Req. 21.6). Una etapa partida al
 * editar metas sigue contando sus semanas desde el inicio original.
 */
export function posicionEnPlan(
  etapas: readonly EtapaConFechas[],
  fecha: FechaLocal,
): PosicionEnPlan | null {
  const etapa = etapaDe(etapas, fecha);
  if (etapa === null) return null;
  const delPlan = etapas.filter((e) => e.planId === etapa.planId);
  const partes = delPlan.filter((e) => e.ciclo === etapa.ciclo && e.indice === etapa.indice);
  const inicio = partes.reduce((min, e) => (e.fechaInicio < min ? e.fechaInicio : min), etapa.fechaInicio);
  const fin = finProgramado(partes);
  const inicioCiclo = delPlan
    .filter((e) => e.ciclo === etapa.ciclo)
    .reduce((min, e) => (e.fechaInicio < min ? e.fechaInicio : min), inicio);
  const siguiente =
    fin === null ? null : (delPlan.find((e) => e.fechaInicio === sumarDias(fin, 1))?.tipo ?? null);

  return {
    planId: etapa.planId,
    tipo: etapa.tipo,
    ciclo: etapa.ciclo,
    indice: etapa.indice,
    inicio,
    semana: Math.floor(diferenciaDias(inicio, fecha) / 7) + 1,
    semanasTotales: fin === null ? null : Math.ceil((diferenciaDias(inicio, fin) + 1) / 7),
    diaDelCiclo: diferenciaDias(inicioCiclo, fecha) + 1,
    diasQueFaltan: fin === null ? null : diferenciaDias(fecha, fin) + 1,
    siguiente,
    empiezaHoy: inicio === fecha,
  };
}

/**
 * "Déficit · semana 3 de 6", para la pastilla del encabezado (0.11.0). El
 * detalle completo (el día del ciclo y lo que falta) sigue en
 * `textoPosicion`, donde hay espacio para leerlo.
 */
export function textoPosicionCorto(p: PosicionEnPlan): string {
  const semana = p.semanasTotales === null ? `semana ${p.semana}` : `semana ${p.semana} de ${p.semanasTotales}`;
  return `${NOMBRE_ETAPA[p.tipo]} · ${semana}`;
}

/** "Déficit · semana 2 de 6 · día 10 del ciclo · faltan 33 días para mantenimiento" */
export function textoPosicion(p: PosicionEnPlan): string {
  const partes = [
    NOMBRE_ETAPA[p.tipo],
    p.semanasTotales === null ? `semana ${p.semana}` : `semana ${p.semana} de ${p.semanasTotales}`,
    `día ${p.diaDelCiclo} del ciclo`,
  ];
  if (p.diasQueFaltan !== null) {
    const dias = p.diasQueFaltan === 1 ? 'falta 1 día' : `faltan ${p.diasQueFaltan} días`;
    partes.push(
      p.siguiente === null
        ? `${dias} para terminar lo programado`
        : `${dias} para ${NOMBRE_ETAPA[p.siguiente].toLowerCase()}`,
    );
  }
  return partes.join(' · ');
}

/**
 * Identifica una etapa aunque esté partida en varios registros por editar sus
 * metas: el plan, el ciclo y su lugar en la plantilla.
 */
export function claveDeEtapa(e: Pick<Stage, 'planId' | 'ciclo' | 'indice'>): string {
  return `${e.planId}:${e.ciclo}:${e.indice}`;
}

export interface AvisoEtapaNueva {
  clave: string;
  tipo: TipoEtapa;
  inicio: FechaLocal;
  empiezaHoy: boolean;
}

/**
 * El aviso de que empezó una etapa (Req. 21.8): desde su primer día hasta que
 * se marca como visto, a lo más durante una semana, para que no se pierda si
 * ese día no se abrió la app. Partir la etapa al editar metas no es una etapa
 * nueva, y no se avisa de la que ya había empezado cuando se creó el plan:
 * quien lo creó ya la conoce. `planCreadoEl` es el día local de creación.
 */
export function avisoDeEtapaNueva(
  posicion: PosicionEnPlan | null,
  planCreadoEl: FechaLocal | null,
  fecha: FechaLocal,
  visto: string | null,
): AvisoEtapaNueva | null {
  if (posicion === null) return null;
  const dias = diferenciaDias(posicion.inicio, fecha);
  if (dias < 0 || dias >= DIAS_AVISO_ETAPA) return null;
  if (planCreadoEl !== null && planCreadoEl >= posicion.inicio) return null;
  const clave = claveDeEtapa(posicion);
  if (clave === visto) return null;
  return { clave, tipo: posicion.tipo, inicio: posicion.inicio, empiezaHoy: dias === 0 };
}

/**
 * Aviso de que lo programado se acaba (Req. 21.4): desde 28 días antes del
 * último día, contando ese día. null si no aplica.
 */
export function avisoFinDeProgramacion(
  plan: Pick<Plan, 'repetir' | 'estado'>,
  etapas: readonly Pick<Stage, 'fechaFin'>[],
  fecha: FechaLocal,
): { fin: FechaLocal; diasQueFaltan: number } | null {
  if (!plan.repetir || plan.estado !== 'activo' || etapas.length === 0) return null;
  const fin = finProgramado(etapas);
  if (fin === null || fecha < sumarDias(fin, -(DIAS_AVISO_FIN - 1))) return null;
  return { fin, diasQueFaltan: Math.max(0, diferenciaDias(fecha, fin) + 1) };
}

/** Los ciclos que siguen a lo ya programado, desde el día siguiente al último. */
export function siguientesCiclos(
  plan: Pick<Plan, 'plantilla' | 'repetir'>,
  etapas: readonly Pick<Stage, 'ciclo' | 'fechaFin'>[],
): EtapaProgramada[] {
  const fin = finProgramado(etapas);
  if (fin === null) throw new RangeError('El plan tiene una etapa sin fin: no hay ciclos que agregar.');
  const ultimoCiclo = etapas.reduce((max, e) => Math.max(max, e.ciclo), 0);
  const inicio = sumarDias(fin, 1);
  return programar(plan.plantilla, plan.repetir, inicio, { cicloInicial: ultimoCiclo + 1 });
}

// ---------------------------------------------------------------------------
// Traslapes (Req. 21.11)
// ---------------------------------------------------------------------------

type Intervalo = Pick<Stage, 'fechaInicio' | 'fechaFin'>;

export function seTraslapan(a: Intervalo, b: Intervalo): boolean {
  const finA = a.fechaFin ?? '9999-12-31';
  const finB = b.fechaFin ?? '9999-12-31';
  return a.fechaInicio <= finB && b.fechaInicio <= finA;
}

/** La primera etapa de otro plan que se traslapa con las nuevas, o null. */
export function primerTraslape<T extends Intervalo>(
  nuevas: readonly Intervalo[],
  otras: readonly T[],
): T | null {
  for (const otra of otras) {
    if (nuevas.some((n) => seTraslapan(n, otra))) return otra;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Terminar un plan sin borrar el pasado (Req. 21.5, 21.13)
// ---------------------------------------------------------------------------

export interface CorteDePlan {
  /** Etapas que empiezan después del último día: todavía no ocurren. */
  borrar: string[];
  /** La etapa en curso termina el último día. */
  recortar: Array<{ id: string; fechaFin: FechaLocal }>;
}

export function terminarEl(etapas: readonly Pick<Stage, 'id' | 'fechaInicio' | 'fechaFin'>[], ultimoDia: FechaLocal): CorteDePlan {
  const corte: CorteDePlan = { borrar: [], recortar: [] };
  for (const e of etapas) {
    if (e.fechaInicio > ultimoDia) corte.borrar.push(e.id);
    else if (e.fechaFin === null || e.fechaFin > ultimoDia) corte.recortar.push({ id: e.id, fechaFin: ultimoDia });
  }
  return corte;
}

// ---------------------------------------------------------------------------
// Cambiar el fin de una etapa (design.md, 8.8; Req. 21.9)
// ---------------------------------------------------------------------------

/** Una etapa completa, aunque esté partida en varios registros. */
export interface EtapaCompleta {
  ciclo: number;
  indice: number;
  tipo: TipoEtapa;
  inicio: FechaLocal;
  fin: FechaLocal | null;
}

/** Lo que hace falta de una etapa para saber su inicio, su fin y su tipo. */
type EtapaConLugar = Pick<EtapaConFechas, 'planId' | 'ciclo' | 'indice' | 'tipo' | 'fechaInicio' | 'fechaFin'>;

/** Las etapas de un plan con su inicio y su fin completos, en orden. */
export function etapasCompletas(etapasDelPlan: readonly Omit<EtapaConLugar, 'planId'>[]): EtapaCompleta[] {
  const porEtapa = new Map<string, Omit<EtapaConLugar, 'planId'>[]>();
  for (const e of etapasDelPlan) {
    const clave = `${e.ciclo}:${e.indice}`;
    porEtapa.set(clave, [...(porEtapa.get(clave) ?? []), e]);
  }
  const completas: EtapaCompleta[] = [];
  for (const partes of porEtapa.values()) {
    const primera = partes.reduce((min, e) => (e.fechaInicio < min.fechaInicio ? e : min));
    completas.push({
      ciclo: primera.ciclo,
      indice: primera.indice,
      tipo: primera.tipo,
      inicio: primera.fechaInicio,
      fin: finProgramado(partes),
    });
  }
  return completas.sort((a, b) => a.inicio.localeCompare(b.inicio));
}

/**
 * Las etapas de todos los planes como franjas de la gráfica de peso (Req.
 * 16.5): una por etapa completa, aunque esté partida por un cambio de metas.
 */
export function franjasDeEtapas(etapas: readonly EtapaConLugar[]): FranjaGrafica[] {
  const porPlan = new Map<string, EtapaConLugar[]>();
  for (const e of etapas) porPlan.set(e.planId, [...(porPlan.get(e.planId) ?? []), e]);
  return [...porPlan.values()]
    .flatMap((delPlan) =>
      etapasCompletas(delPlan).map((e) => ({ desde: e.inicio, hasta: e.fin, tono: e.tipo, etiqueta: NOMBRE_ETAPA[e.tipo] })),
    )
    .sort((a, b) => a.desde.localeCompare(b.desde));
}

export interface EtapaCambiable extends EtapaCompleta {
  fin: FechaLocal;
  enCurso: boolean;
  /** Terminó ayer: se puede alargar, por ejemplo para deshacer un cambio de hoy. */
  terminoAyer: boolean;
}

/**
 * Las etapas a las que se les puede cambiar el fin: la que terminó ayer, la
 * que va hoy y las que vienen, si tienen fecha de fin. Las demás ya pasaron.
 * La que terminó ayer se puede alargar sin tocar ningún día pasado (el nuevo
 * fin es ayer o después): así un cambio de hoy tiene vuelta atrás, y el
 * primer día de una etapa todavía se puede seguir con la anterior.
 */
export function etapasConFinCambiable(etapasDelPlan: readonly EtapaConFechas[], hoy: FechaLocal): EtapaCambiable[] {
  const ayer = sumarDias(hoy, -1);
  return etapasCompletas(etapasDelPlan).flatMap((e) =>
    e.fin === null || e.fin < ayer
      ? []
      : [{ ...e, fin: e.fin, enCurso: e.inicio <= hoy && e.fin >= hoy, terminoAyer: e.fin === ayer }],
  );
}

export interface CambioDeFin<T> {
  /** Días que se recorren las etapas siguientes: positivo, más tarde; negativo, antes. */
  dias: number;
  actualizar: Array<{ id: string; cambios: Pick<Stage, 'fechaInicio' | 'fechaFin'> }>;
  /**
   * Partes de la etapa que empezaban después del nuevo fin (metas editadas
   * para días que ahora son de la etapa siguiente): se quitan.
   */
  borrar: string[];
  /** Todas las etapas del plan como quedan, para validar y mostrar. */
  etapas: T[];
}

/**
 * Cambia el último día de una etapa y recorre los mismos días las siguientes
 * del plan, para que no queden huecos ni traslapes (Req. 21.9). Lo que ya
 * pasó no cambia: el nuevo fin es ayer o después y nunca antes del inicio de
 * la etapa, y solo se cambian la etapa en curso, las futuras y la que terminó
 * ayer. Cada etapa conserva sus metas; los días que cambian de etapa toman
 * las de su etapa nueva.
 */
export function recorrerAlCambiarFin<T extends EtapaConFechas & Pick<Stage, 'id'>>(
  etapasDelPlan: readonly T[],
  etapa: { ciclo: number; indice: number },
  nuevoFin: FechaLocal,
  hoy: FechaLocal,
): CambioDeFin<T> {
  const partes = etapasDelPlan
    .filter((e) => e.ciclo === etapa.ciclo && e.indice === etapa.indice)
    .sort((a, b) => a.fechaInicio.localeCompare(b.fechaInicio));
  const primera = partes[0];
  if (primera === undefined) throw new RangeError('Esa etapa no existe en el plan.');
  const finActual = finProgramado(partes);
  if (finActual === null) throw new RangeError('Esa etapa no tiene fecha de fin, así que no hay etapas que recorrer.');
  if (finActual < sumarDias(hoy, -1)) throw new RangeError('Esa etapa ya terminó: lo que ya pasó no cambia.');
  if (!esFechaLocal(nuevoFin)) throw new RangeError('La fecha no es válida.');
  if (nuevoFin < sumarDias(hoy, -1)) {
    throw new RangeError('El último día puede ser ayer o después: los días que ya pasaron conservan su etapa y sus metas.');
  }
  if (nuevoFin < primera.fechaInicio) {
    throw new RangeError(
      `La etapa empieza el ${formatearCortoConAnio(primera.fechaInicio)}: su último día no puede ser antes.`,
    );
  }
  if (diferenciaDias(primera.fechaInicio, nuevoFin) + 1 > SEMANAS_MAXIMAS_ETAPA * 7) {
    throw new RangeError(`Una etapa dura como máximo ${SEMANAS_MAXIMAS_ETAPA} semanas.`);
  }

  const dias = diferenciaDias(finActual, nuevoFin);
  const cambio: CambioDeFin<T> = { dias, actualizar: [], borrar: [], etapas: [] };
  if (dias === 0) return { ...cambio, etapas: [...etapasDelPlan] };

  const deLaEtapa = new Set(partes.map((p) => p.id));
  // La primera parte siempre queda: el nuevo fin no es antes de su inicio
  const quedan = partes.filter((p) => p.fechaInicio <= nuevoFin);
  const ultima = quedan[quedan.length - 1] ?? primera;

  for (const e of etapasDelPlan) {
    let cambios: Pick<Stage, 'fechaInicio' | 'fechaFin'> | null = null;
    if (deLaEtapa.has(e.id)) {
      if (e.fechaInicio > nuevoFin) {
        cambio.borrar.push(e.id);
        continue;
      }
      if (e.id === ultima.id && e.fechaFin !== nuevoFin) cambios = { fechaInicio: e.fechaInicio, fechaFin: nuevoFin };
    } else if (e.fechaInicio > finActual) {
      cambios = {
        fechaInicio: sumarDias(e.fechaInicio, dias),
        fechaFin: e.fechaFin === null ? null : sumarDias(e.fechaFin, dias),
      };
    }
    if (cambios === null) {
      cambio.etapas.push(e);
    } else {
      cambio.actualizar.push({ id: e.id, cambios });
      cambio.etapas.push({ ...e, ...cambios });
    }
  }
  return cambio;
}

// ---------------------------------------------------------------------------
// Editar metas desde un día (design.md, 8.9; Req. 21.12)
// ---------------------------------------------------------------------------

export interface CambioDeMetas {
  /** Etapas existentes con cambios: la que se parte termina el día anterior. */
  actualizar: Array<{ id: string; cambios: Partial<Pick<Stage, 'fechaFin'> & MetasEtapa> }>;
  /** La segunda parte de la etapa partida, desde el día del cambio. */
  crear: EtapaProgramada[];
  /** La plantilla del plan con las metas nuevas, para los ciclos que se agreguen después. */
  plantilla: EtapaPlantilla[];
}

/**
 * Las metas nuevas de un tipo de etapa (su posición en la plantilla) aplican
 * desde `desde`: la etapa en curso se parte, las futuras toman las metas
 * nuevas y las pasadas no se tocan.
 */
export function cambiarMetasDesde(
  plan: Pick<Plan, 'plantilla'>,
  etapas: readonly (EtapaConFechas & Pick<Stage, 'id'>)[],
  indice: number,
  desde: FechaLocal,
  metas: MetasEtapa,
): CambioDeMetas {
  const base = plan.plantilla[indice];
  if (base === undefined) throw new RangeError('Esa etapa no existe en el plan.');
  const error = validarMetas(metas);
  if (error !== null) throw new RangeError(error);

  const cambio: CambioDeMetas = {
    actualizar: [],
    crear: [],
    plantilla: plan.plantilla.map((p, i) => (i === indice ? { ...p, ...metas } : p)),
  };
  for (const e of etapas) {
    if (e.indice !== indice) continue;
    if (e.fechaFin !== null && e.fechaFin < desde) continue; // ya pasó
    if (e.fechaInicio >= desde) {
      cambio.actualizar.push({ id: e.id, cambios: { ...metas } });
      continue;
    }
    // Empezó antes y sigue: se parte en dos
    cambio.actualizar.push({ id: e.id, cambios: { fechaFin: sumarDias(desde, -1) } });
    cambio.crear.push({
      ciclo: e.ciclo,
      indice: e.indice,
      tipo: e.tipo,
      fechaInicio: desde,
      fechaFin: e.fechaFin,
      ...metas,
    });
  }
  return cambio;
}

// ---------------------------------------------------------------------------
// Mantenimiento vigente y metas del día (design.md, 7.7 y 8.9)
// ---------------------------------------------------------------------------

/**
 * El mantenimiento vigente en una fecha: el de mayor `desde` ≤ fecha que no
 * se rechazó. Con la misma fecha cuenta el capturado después: así se corrige
 * un valor sin borrar el anterior.
 */
export function mantenimientoVigente<
  T extends Pick<Maintenance, 'desde' | 'decision' | 'kcal' | 'creadoEn'>,
>(registros: readonly T[], fecha: FechaLocal): T | null {
  let vigente: T | null = null;
  for (const r of registros) {
    if (r.desde > fecha || r.decision === 'rechazada' || r.kcal === null) continue;
    if (
      vigente === null ||
      r.desde > vigente.desde ||
      (r.desde === vigente.desde && r.creadoEn > vigente.creadoEn)
    ) {
      vigente = r;
    }
  }
  return vigente;
}

export interface MetasManuales {
  kcal: number | null;
  proteinaG: number | null;
  carbohidratosG: number | null;
  grasaG: number | null;
  ayunoHoras: number | null;
}

export const SIN_METAS_MANUALES: Readonly<MetasManuales> = Object.freeze({
  kcal: null,
  proteinaG: null,
  carbohidratosG: null,
  grasaG: null,
  ayunoHoras: null,
});

/** Las metas manuales con su historial: las de ahora aplican desde `desde`. */
export interface HistorialMetasManuales {
  actuales: MetasManuales;
  /** null = desde siempre. */
  desde: FechaLocal | null;
  anteriores: readonly PeriodoMetasManuales[];
}

/**
 * Las metas manuales que aplicaban en una fecha (Req. 18.10). Cambiar las
 * metas aplica desde hoy; un día anterior conserva las que tenía, y un día
 * antes de las primeras metas no tiene meta.
 */
export function metasManualesVigentes(h: HistorialMetasManuales, fecha: FechaLocal): MetasManuales {
  if (h.desde === null || fecha >= h.desde) return h.actuales;
  const periodo = h.anteriores.find((p) => (p.desde === null || p.desde <= fecha) && fecha <= p.hasta);
  if (periodo === undefined) return SIN_METAS_MANUALES;
  const { kcal, proteinaG, carbohidratosG, grasaG, ayunoHoras } = periodo;
  return { kcal, proteinaG, carbohidratosG, grasaG, ayunoHoras };
}

const tieneAlgunaMeta = (m: MetasManuales) => Object.values(m).some((v) => v !== null);

const mismasMetas = (a: MetasManuales, b: MetasManuales) =>
  a.kcal === b.kcal &&
  a.proteinaG === b.proteinaG &&
  a.carbohidratosG === b.carbohidratosG &&
  a.grasaG === b.grasaG &&
  a.ayunoHoras === b.ayunoHoras;

/**
 * El historial después de cambiar las metas manuales hoy. Las de antes pasan
 * al historial hasta ayer; si ya se habían cambiado hoy, solo se reemplazan.
 * null si no cambió nada.
 */
export function cambiarMetasManuales(
  h: HistorialMetasManuales,
  nuevas: MetasManuales,
  hoy: FechaLocal,
): HistorialMetasManuales | null {
  if (mismasMetas(h.actuales, nuevas)) return null;
  const anteriores = [...h.anteriores];
  const yaEmpezaronAntes = h.desde === null || h.desde < hoy;
  if (yaEmpezaronAntes && tieneAlgunaMeta(h.actuales)) {
    anteriores.push({ desde: h.desde, hasta: sumarDias(hoy, -1), ...h.actuales });
  }
  return { actuales: { ...nuevas }, desde: hoy, anteriores };
}

export interface MetasDelDia {
  /** 'etapa' si un plan cubre el día; si no, las metas manuales de Ajustes (Req. 18.10). */
  origen: 'etapa' | 'manual';
  etapa: EtapaConFechas | null;
  kcal: number | null;
  /** La etapa pide una meta relativa al mantenimiento y no hay uno capturado. */
  faltaMantenimiento: boolean;
  /** El mantenimiento usado en el cálculo, para explicarlo. */
  mantenimientoKcal: number | null;
  proteinaG: number | null;
  carbohidratosG: number | null;
  grasaG: number | null;
  /**
   * Al menos un macro salió del mantenimiento aceptado, no de la etapa
   * (0.5.1). Solo pasa en etapas de mantenimiento.
   */
  macrosDeMantenimiento: boolean;
  ayunoHoras: number | null;
}

/** kcal de una meta de etapa con el mantenimiento vigente (Req. 22.8: a 10 kcal). */
export function kcalDeMeta(
  meta: MetaKcal | null,
  mantenimiento: number | null,
): { kcal: number | null; faltaMantenimiento: boolean } {
  if (meta === null) return { kcal: null, faltaMantenimiento: false };
  if (meta.modo === 'fija') return { kcal: meta.kcal, faltaMantenimiento: false };
  if (mantenimiento === null) return { kcal: null, faltaMantenimiento: true };
  const kcal =
    meta.modo === 'diferencia' ? mantenimiento + meta.kcal : mantenimiento * (1 + meta.pct / 100);
  return { kcal: redondearAMultiplo(kcal, 10), faltaMantenimiento: false };
}

export function metasDelDia(
  fecha: FechaLocal,
  etapas: readonly EtapaConFechas[],
  mantenimientos: readonly Pick<Maintenance, 'desde' | 'decision' | 'kcal' | 'creadoEn' | 'macros'>[],
  manuales: MetasManuales,
): MetasDelDia {
  const etapa = etapaDe(etapas, fecha);
  if (etapa === null) {
    return {
      origen: 'manual',
      etapa: null,
      kcal: manuales.kcal,
      faltaMantenimiento: false,
      mantenimientoKcal: null,
      proteinaG: manuales.proteinaG,
      carbohidratosG: manuales.carbohidratosG,
      grasaG: manuales.grasaG,
      macrosDeMantenimiento: false,
      ayunoHoras: manuales.ayunoHoras,
    };
  }
  const registro = mantenimientoVigente(mantenimientos, fecha);
  const mantenimiento = registro?.kcal ?? null;
  const { kcal, faltaMantenimiento } = kcalDeMeta(etapa.metaKcal, mantenimiento);
  // Los macros aceptados con el mantenimiento (0.5.1) solo llenan los huecos
  // de una etapa de mantenimiento: un déficit conserva los suyos siempre.
  const deMantenimiento = etapa.tipo === 'mantenimiento' ? (registro?.macros ?? null) : null;
  const macro = (deLaEtapa: number | null, cual: keyof MacrosDiarios) =>
    deLaEtapa !== null || deMantenimiento === null ? deLaEtapa : deMantenimiento[cual];
  const proteinaG = macro(etapa.proteinaG, 'proteinaG');
  const carbohidratosG = macro(etapa.carbohidratosG, 'carbohidratosG');
  const grasaG = macro(etapa.grasaG, 'grasaG');
  return {
    origen: 'etapa',
    etapa,
    kcal,
    faltaMantenimiento,
    mantenimientoKcal: etapa.metaKcal === null || etapa.metaKcal.modo === 'fija' ? null : mantenimiento,
    proteinaG,
    carbohidratosG,
    grasaG,
    macrosDeMantenimiento:
      deMantenimiento !== null &&
      (etapa.proteinaG === null || etapa.carbohidratosG === null || etapa.grasaG === null),
    ayunoHoras: etapa.ayunoHoras,
  };
}

/** "1,500 kcal fijas", "tu mantenimiento − 400 kcal", "tu mantenimiento", "tu mantenimiento + 10 %" */
export function textoMetaKcal(meta: MetaKcal | null): string {
  if (meta === null) return 'Sin meta de kcal';
  if (meta.modo === 'fija') return `${formatearNumero(meta.kcal, { decimales: 0 })} kcal fijas`;
  const valor = meta.modo === 'diferencia' ? meta.kcal : meta.pct;
  if (valor === 0) return 'Igual a tu mantenimiento';
  const signo = valor > 0 ? '+' : '−';
  const cifra = formatearNumero(Math.abs(valor), { decimales: 0 });
  return `Tu mantenimiento ${signo} ${cifra}${meta.modo === 'diferencia' ? ' kcal' : ' %'}`;
}

/**
 * Las metas de un día en palabras, para el aviso de etapa nueva (Req. 21.8):
 * las que tiene y las que no tiene. Las kcal que dependen de un mantenimiento
 * sin capturar no van en ninguna de las dos: se avisan aparte.
 */
export function metasEnPalabras(m: MetasDelDia): { tiene: string[]; sinMeta: string[] } {
  const tiene: string[] = [];
  const sinMeta: string[] = [];
  if (m.kcal !== null) tiene.push(formatearKcal(m.kcal));
  else if (!m.faltaMantenimiento) sinMeta.push('calorías');
  const gramos: Array<[string, number | null]> = [
    ['proteína', m.proteinaG],
    ['carbohidratos', m.carbohidratosG],
    ['grasa', m.grasaG],
  ];
  for (const [nombre, g] of gramos) {
    if (g === null) sinMeta.push(nombre);
    else tiene.push(`${nombre} ${formatearGramos(g)}`);
  }
  if (m.ayunoHoras === null) sinMeta.push('ayuno');
  else tiene.push(`ayuno de ${formatearNumero(m.ayunoHoras, { decimales: 1, fijos: false })} h`);
  return { tiene, sinMeta };
}
